import React from 'react';
import {
    type AccessibilityRole,
    type NativeSyntheticEvent,
    Pressable,
    StyleSheet,
    View
} from 'react-native';

import { ThemeColor } from '@lib/constants/Colors';
import useBreakpoints, { Breakable } from '@lib/hooks/useBreakpoints';
import useShorthandStyles, { ResponsiveShorthand, Sx, useSx } from '@lib/hooks/useShorthandStyles';
import useThemeColor from '@lib/hooks/useThemeColor';

type FlexDirection = 'row' | 'column' | 'row-reverse' | 'column-reverse';

export type StackProps = {
    // this is annoying. the react-native type does not include "form"
    // so we have to fudge it and cast here in order to get rid of
    // browser warnings for form fields that are not enclosed in forms.
    accessibilityRole?: AccessibilityRole | 'form';
    direction?: Breakable<FlexDirection> | FlexDirection;
    /**
     * When `true` use the default theme background
     */
    background?: boolean;
    backgroundColor?: ThemeColor;

    borderColor?: ThemeColor;
    border?: true | number;
    fill?: boolean;
    /**
     * Will turn this stack into a link button
     */
    href?: string;
    /**
     * Will wrap the stack in a TouchableWithoutFeedback with this onPress handler
     */
    onPress?: (e: NativeSyntheticEvent<unknown>) => void;
    /**
     * `ResponsiveShorthand` props can be passed either as direct props, e.g.
     *
     * ```js
     * <Stack p={{ sm: 20, md: 50 }} />
     * ```
     *
     * Or in the `sx` prop:
     *
     * ```js
     * <Stack sx={{ sm: { p: 20 }, md: { p: 50 } }} />
     * ```
     *
     * Also takes bare shorthand values
     *
     * ```js
     * <Stack sx={{ p: 20 }} />
     * ```
     */
    sx?: Sx;
    disabled?: boolean;
} & Omit<ResponsiveShorthand, 'direction'> &
    Omit<View['props'], 'accessibilityRole'>;
/**
# Stack
The base layout component for the Just Salad app. A wrapper around react-native View
which provides responsive shorthand properties via `useShorthandStyles`

## Props

- `accessibilityRole`: AccessibilityRole | 'form'
- `direction`: Breakable<FlexDirection> | FlexDirection (default: 'column'), prefer `row` boolean
- `background`: boolean - Use theme background, transparent by default
- `backgroundColor`: ThemeColor
- `borderColor`: ThemeColor (default: 'border')
- `border`: true | number
- `fill`: boolean - Apply absolute fill
- `href`: string - Turn stack into a link button
- `onPress`: (e: NativeSyntheticEvent<unknown>) => void
- `sx`: Sx - Shorthand styles object
- ...ResponsiveShorthand props
- ...View props (excluding 'accessibilityRole')

## Usage

```jsx
import Stack from '@components/Stack';

<Stack
  row
  background
  p={{ sm: 20, md: 50 }}
  onPress={() => console.log('Pressed')}
>
    {children}
</Stack>
```

## Features

- Responsive layout with breakpoint-based direction
- Theme-aware background and border colors
- Shorthand styling props
- Pressable functionality
- Memoized
*/
const Stack = React.forwardRef<View, StackProps>((props, ref) => {
    const {
        accessibilityRole,
        background,
        backgroundColor: bg,
        border,
        borderColor = 'border',
        children,
        direction: breakableDirection = 'column',
        style,
        onPress,
        sx = {},
        fill,
        disabled = false,
        ...otherProps
    } = props;

    // 2. if `backgroundColor` is set use that; otherwise
    // 1. if `background` is `true`, use the default background; orstack
    // 3. default to transparency.
    const backgroundColor = useThemeColor(bg ? bg : background ? 'background' : 'transparent');
    const expandedStyle = useShorthandStyles(otherProps);
    const expandedSx = useSx(sx);
    const direction = useBreakpoints(breakableDirection);

    const borderStyle = {
        borderWidth: border === true ? 1 : border,
        borderColor: useThemeColor(borderColor ?? 'border')
    };
    const fillStyle = fill ? StyleSheet.absoluteFill : {};

    const BareView = (
        <View
            accessibilityRole={accessibilityRole as AccessibilityRole}
            accessibilityHidden={otherProps.accessibilityElementsHidden}
            ref={ref}
            style={[
                { backgroundColor, flexDirection: direction },
                borderStyle,
                expandedStyle,
                expandedSx,
                fillStyle,
                style
            ]}
            {...otherProps}
        >
            {children}
        </View>
    );

    if (!onPress) {
        return BareView;
    }

    return (
        <Pressable accessibilityRole="button" onPress={onPress} disabled={disabled}>
            {BareView}
        </Pressable>
    );
});

export default React.memo(Stack);
